import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Trash2, Check, Eye } from "lucide-react";

function formatDate(iso) { try { return new Date(iso).toLocaleString("de-DE"); } catch { return ""; } }

const STATUS_LABELS = {
  new: { label: "Neu", color: "text-[hsl(var(--brand-green-strong))] bg-[hsl(var(--brand-soft))]" },
  read: { label: "Gelesen", color: "text-neutral-600 bg-neutral-100" },
  done: { label: "Erledigt", color: "text-neutral-500 bg-neutral-100 line-through" },
};

const CATEGORIES = ["Alle", "Allgemein", "Verkehr", "Bauphase", "Beteiligung"];
const STATUSES = ["Alle", "new", "read", "done"];

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [category, setCategory] = useState("Alle");
  const [status, setStatus] = useState("Alle");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (category !== "Alle") params.category = category;
      if (status !== "Alle") params.status = status;
      const { data } = await api.get("/contacts", { params });
      setItems(data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [category, status]);

  async function setStatusFor(id, newStatus) {
    try { await api.put(`/contacts/${id}/status`, null, { params: { status: newStatus } }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  async function del(id) {
    if (!window.confirm("Anliegen wirklich löschen?")) return;
    try { await api.delete(`/contacts/${id}`); setActive(null); load(); toast.success("Gelöscht"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  function openDetail(it) {
    setActive(it);
    if (it.status === "new") setStatusFor(it.id, "read");
  }

  return (
    <div data-testid="admin-contacts">
      <h1 className="font-semibold text-3xl mb-1">Bürger-Anliegen</h1>
      <p className="text-sm text-neutral-500 mb-8">Eingegangene Nachrichten über das öffentliche Kontaktformular.</p>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <span>Kategorie:</span>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`filter-category-${c}`}
              className={`px-3 py-1 text-xs border ${category === c ? "bg-[hsl(var(--brand-green-dark))] text-white border-[hsl(var(--brand-green-dark))]" : "border-brand-line text-neutral-700 hover:bg-neutral-50"}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-500 ml-auto">
          <span>Status:</span>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`filter-status-${s}`}
              className={`px-3 py-1 text-xs border ${status === s ? "bg-[hsl(var(--brand-green-dark))] text-white border-[hsl(var(--brand-green-dark))]" : "border-brand-line text-neutral-700 hover:bg-neutral-50"}`}
            >
              {s === "new" ? "Neu" : s === "read" ? "Gelesen" : s === "done" ? "Erledigt" : "Alle"}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-brand-line">
        {loading ? <p className="p-8 text-sm text-neutral-500">Lade…</p> :
        items.length === 0 ? <p className="p-8 text-sm text-neutral-500">Noch keine Anliegen eingegangen.</p> : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-5 py-3 font-normal">Status</th>
                <th className="text-left px-5 py-3 font-normal">Kategorie</th>
                <th className="text-left px-5 py-3 font-normal">Name</th>
                <th className="text-left px-5 py-3 font-normal">Betreff</th>
                <th className="text-left px-5 py-3 font-normal">Eingegangen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-brand-line hover:bg-neutral-50 cursor-pointer" data-testid={`contact-row-${c.id}`} onClick={() => openDetail(c)}>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs ${STATUS_LABELS[c.status]?.color || ""}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      {STATUS_LABELS[c.status]?.label || c.status}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-block px-2 py-0.5 text-xs border border-brand-line text-neutral-600">{c.category || "Allgemein"}</span>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-xs text-neutral-500">{c.email}</p>
                  </td>
                  <td className="px-5 py-4 text-neutral-700">{c.subject || <span className="text-neutral-400">—</span>}</td>
                  <td className="px-5 py-4 text-neutral-600">{formatDate(c.created_at)}</td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={(e) => { e.stopPropagation(); openDetail(c); }} className="text-neutral-500 hover:text-[hsl(var(--brand-green-strong))]" title="Anzeigen"><Eye className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setActive(null)}>
          <div className="bg-white max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="contact-detail">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-semibold text-2xl mb-1">{active.subject || "Ohne Betreff"}</h2>
                <p className="text-sm text-neutral-500">{active.name} · <a href={`mailto:${active.email}`} className="text-[hsl(var(--brand-green-strong))] underline">{active.email}</a></p>
                <p className="text-xs text-neutral-400 mt-1">{formatDate(active.created_at)} · Kategorie: <span className="text-neutral-600">{active.category || "Allgemein"}</span></p>
              </div>
              <button onClick={() => setActive(null)} className="text-neutral-400 hover:text-neutral-900 text-xl">×</button>
            </div>
            <div className="border-t border-brand-line pt-5 text-sm text-neutral-700 whitespace-pre-line leading-relaxed mb-6">
              {active.message}
            </div>
            <div className="flex gap-3 justify-end">
              <a href={`mailto:${active.email}?subject=Re: ${encodeURIComponent(active.subject || "Ihr Anliegen")}`} className="inline-flex items-center gap-2 border border-brand-line px-4 py-2 text-sm hover:bg-neutral-50">
                <Mail className="h-4 w-4" /> Antworten
              </a>
              {active.status !== "done" && (
                <button onClick={() => { setStatusFor(active.id, "done"); setActive(null); }} data-testid="contact-mark-done" className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-4 py-2 text-sm hover:bg-[hsl(var(--brand-green-strong))]">
                  <Check className="h-4 w-4" /> Als erledigt markieren
                </button>
              )}
              <button onClick={() => del(active.id)} className="inline-flex items-center gap-2 text-red-600 border border-red-200 px-4 py-2 text-sm hover:bg-red-50">
                <Trash2 className="h-4 w-4" /> Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
